import React from 'react';
import { Eye } from 'lucide-react';
import { useFontSizes } from '../../contexts/FontSizeContext';
import { useI18n } from '../../i18n/I18nContext';
import { ProductCard } from '../ProductCard';
import { safeUrl, openInWorkingTab } from '../../utils/navigate';
import type { WidgetProps } from './types';

export const RecentlyViewedWidget: React.FC<WidgetProps> = ({
  config,
  recentProducts = [],
  onProductClick,
}) => {
  const { heading, body, small } = useFontSizes();
  const { t, lang } = useI18n();
  const locale = lang === 'en' ? 'en-IE' : 'el-GR';

  // Only surface items the user can actually buy. inStock already reflects the
  // feed's authoritative `canBeBought` flag, so trust it (some buyable items
  // are priced 0, e.g. accessories priced at checkout).
  const available = recentProducts.filter((p) => p.inStock);
  const items = config.maxItems ? available.slice(0, config.maxItems) : available;

  if (items.length === 0) return null;

  const displayMode = config.displayMode ?? 'list';

  return (
    <section className="mb-8 animate-fade-in">
      <h2 className={`flex items-center gap-2 ${heading} font-semibold uppercase tracking-wider text-gray-500 mb-4`}>
        <Eye className="h-4 w-4" />
        {config.title ?? t('defaults.recentlyViewed')}
      </h2>
      {displayMode === 'grid' ? (
        <div className="botilia-hscroll">
          {items.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              compact
              onProductClick={onProductClick}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((product) => (
            <a
              key={product.id}
              href={safeUrl(product.url)}
              onClick={(e) => { onProductClick?.(product); openInWorkingTab(product.url, e); }}
              className="flex items-center gap-3 p-2 hover:bg-gray-50 transition-colors group"
            >
              <div className="w-12 h-12 flex-shrink-0 bg-white overflow-hidden flex items-center justify-center">
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={(lang === 'el' && product.nameEl) ? product.nameEl : product.name}
                    loading="lazy"
                    className="w-full h-full object-contain p-0.5"
                  />
                ) : (
                  <Eye className="h-5 w-5 text-gray-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`${body} font-medium text-gray-900 truncate group-hover:text-brand-ink transition-colors`}>
                  {(lang === 'el' && product.nameEl) ? product.nameEl : product.name}
                </p>
                {product.producer && (
                  <p className={`${small} text-gray-500 truncate`}>{product.producer}</p>
                )}
              </div>
              <span className={`${body} font-bold text-gray-900 flex-shrink-0`}>
                {new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(
                  product.salePrice && product.salePrice < product.price ? product.salePrice : product.price
                )}
              </span>
            </a>
          ))}
        </div>
      )}
    </section>
  );
};
